import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import lodash from 'lodash';

import icons from 'styles/icons/icons.scss';
import styles from './DocumentTypePicker.scss';
import DataType from 'complionAdmin/components/BinderTemplateQuery/DataType/DataType';
import DataTypeEditor from 'common/components/DataTypeEditor/DataTypeEditor';
import Part11Indicator from 'common/components/Part11Indicator/Part11Indicator';

import {
    changeDocumentTypesVisibility,
    changeDocumentTypeFilter,
    highlightDocumentType,
    selectDocumentType,
    deselectDocumentType
} from 'indexDocuments/actions';

import {
    getHighlightedDocumentType
} from 'indexDocuments/selectors';

const noop = () => {};

class DocumentTypePicker extends Component {
    componentDidMount() {
        this.focusEditor();
    }

    componentDidUpdate(prevProps) {
        const {
            documentTypesVisibility: lastDocumentTypesVisibility
        } = prevProps;
        const {
            documentTypesVisibility,
            selectedDocumentType,
            highlightDocumentType
        } = this.props;
        if (lastDocumentTypesVisibility !== documentTypesVisibility) {
            if (selectedDocumentType) {
                highlightDocumentType(selectedDocumentType.id);
            } else {
                this.highlightDocumentTypeByOffset(0);
            }
            if (documentTypesVisibility) {
                this.focusEditor();
            }
        }
    }

    render() {
        const {
            indexingType,
            documentTypes,
            selectedDocumentType,
            documentTypeFilter,
            documentTypesVisibility,
            highlightedDocumentType,
            changeDocumentTypeFilter,
            selectDocumentType,
            deselectDocumentType,
            changeDocumentTypesVisibility,
        } = this.props;

        if (!['initialIndex', 'reindex'].includes(indexingType)) {
            const { displayName } = selectedDocumentType;
            return (
                <div className={styles.documentTypePicker} data-cy="documentTypePicker">
                    <DataType className={styles.disabledDocumentTypeInput}
                        dataType="small_text"
                        value={displayName} />
                </div>
            );
        }

        if (!documentTypesVisibility && selectedDocumentType) {
            return (
                <div className={styles.documentTypePicker}
                    data-cy="documentTypePicker-render"
                    onKeyPress={e => this.handleKeyPress(e)}
                    onKeyDown={e => this.handleKeyDown(e)}>
                    <div className={styles.documentTypeSelectedContainer}>
                        <DataTypeEditor dataType="small_text"
                            className={styles.documentTypeSelectedInput}
                            ref={editor => this.editor = editor}
                            value={selectedDocumentType.displayName}
                            changeHandler={noop}
                            placeholder={'Filter document type by name or description'}
                            onFocus={this.handleFocus.bind(this)} />
                        <div className={styles.clearSelectedDocumentType}
                            onClick={e => deselectDocumentType()}
                            data-cy="documentTypePicker-onClick">
                            <span className={icons.materialIcons}>clear</span>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.documentTypePicker}
                data-cy="documentTypePicker-else"
                onKeyPress={e => this.handleKeyPress(e)}
                onKeyDown={e => this.handleKeyDown(e)}>
                <div className={styles.documentTypePickerHeader}>
                    <Part11Indicator className={styles.part11Icon} />
                    <p className={styles.documentTypePickerLabel}>
                        Select Document Type or Search By Name
                    </p>
                </div>
                <HelpText documentTypes={documentTypes} documentTypeFilter={documentTypeFilter} />
                <div className={styles.documentTypeSelectedContainer}>
                    <DataTypeEditor dataType="small_text"
                        className={styles.documentTypeSelectedInput}
                        ref={editor => this.editor = editor}
                        value={documentTypeFilter}
                        changeHandler={e => changeDocumentTypeFilter(e.target.value)}
                        placeholder={'Filter document type by name or description'}
                        onFocus={this.handleFocus.bind(this)} />
                    <div className={styles.clearSelectedDocumentType}
                        onClick={this.handleClearFilter.bind(this)}
                        data-cy="documentTypePicker-handleClearFilter">
                        <span className={icons.materialIcons}>clear</span>
                    </div>
                </div>
                <p className={styles.documentTypePickerLabel}>Document Types</p>
                <DocumentTypeList documentTypes={documentTypes}
                    highlightedDocumentType={highlightedDocumentType}
                    selectDocumentType={selectDocumentType}
                    changeDocumentTypeFilter={changeDocumentTypeFilter}
                    changeDocumentTypesVisibility={changeDocumentTypesVisibility} />
            </div>
        );
    }

    focusEditor() {
        const { editor } = this;
        if (!editor) {
            return;
        }
        if (typeof editor.select === 'function') {
            editor.select();
        } else if (typeof editor.focus === 'function') {
            editor.focus();
        }
    }

    highlightDocumentTypeByOffset(offset) {
        const {
            highlightedDocumentType,
            documentTypes,
            highlightDocumentType
        } = this.props;
        if (highlightedDocumentType) {
            const documentTypesList = lodash.values(documentTypes);
            const index = lodash.findIndex(documentTypesList, ['id', highlightedDocumentType.id]);
            const nextIndex = lodash.clamp(index + offset, 0, lodash.size(documentTypes) - 1);
            const documentType = documentTypesList[nextIndex];
            if (documentType) {
                highlightDocumentType(documentType.id);
            }
        } else {
            const documentType = lodash.find(documentTypes);
            if (documentType) {
                highlightDocumentType(documentType.id);
            }
        }
    }

    handleClearFilter(e) {
        const {
            changeDocumentTypeFilter
        } = this.props;
        changeDocumentTypeFilter('');
        this.focusEditor();
    }

    handleFocus(e) {
        e.stopPropagation();
        const {
            changeDocumentTypesVisibility,
            changeDocumentTypeFilter,
            selectedDocumentType
        } = this.props;
        if (!selectedDocumentType) {
            changeDocumentTypesVisibility(true);
            changeDocumentTypeFilter('');
        }
    }

    handleKeyPress(e) {
        const {
            selectDocumentType,
            selectedDocumentType,
            highlightedDocumentType,
            documentTypesVisibility,
            changeDocumentTypesVisibility,
            changeDocumentTypeFilter
        } = this.props;
        switch(e.key) {
            case 'Enter':
                if (documentTypesVisibility && highlightedDocumentType) {
                    e.stopPropagation();
                    e.preventDefault();
                    selectDocumentType(highlightedDocumentType.id);
                    changeDocumentTypesVisibility(false);
                    changeDocumentTypeFilter('');
                } else if (documentTypesVisibility && selectedDocumentType) {
                    changeDocumentTypesVisibility(false);
                } else if (!documentTypesVisibility && selectedDocumentType) {
                    changeDocumentTypesVisibility(true);
                    changeDocumentTypeFilter(selectedDocumentType.displayName);
                } else {
                    changeDocumentTypesVisibility(true);
                    changeDocumentTypeFilter('');
                }
                break;
        }
    }

    handleKeyDown(e) {
        const {
            documentTypesVisibility,
            changeDocumentTypesVisibility,
            changeDocumentTypeFilter
        } = this.props;
        switch(e.key) {
            case 'ArrowUp':
                e.stopPropagation();
                e.preventDefault();
                this.highlightDocumentTypeByOffset(-1);
                break;
            case 'ArrowDown':
                e.stopPropagation();
                e.preventDefault();
                this.highlightDocumentTypeByOffset(1);
                break;
            case 'Escape':
                if (documentTypesVisibility) {
                    e.stopPropagation();
                    e.preventDefault();
                    changeDocumentTypesVisibility(false);
                    changeDocumentTypeFilter('');
                }
                break;
        }
    }
}

DocumentTypePicker.propTypes = {
    documentTypesVisibility: PropTypes.bool.isRequired,
    documentTypeFilter: PropTypes.string.isRequired,
    indexingType: PropTypes.string.isRequired,
    documentTypes: PropTypes.objectOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        displayName: PropTypes.string.isRequired
    })),
    selectedDocumentType: PropTypes.shape({
        id: PropTypes.string.isRequired,
        displayName: PropTypes.string.isRequired
    })
};

const HelpText = ({ documentTypes, documentTypeFilter }) => {
    if (lodash.size(documentTypes) === 0 && !lodash.isNil(documentTypeFilter) && !lodash.isEmpty(documentTypeFilter)) {
        return (
            <span className={styles.documentTypeHelpTextWarning} data-cy="documentTypePicker-Warning">
                Warning: There are no Document Types that match the search text.
            </span>
        );
    } else {
        return (
            <span className={styles.documentTypeHelpTextSuccess} data-cy="documentTypePicker-Success">
                Use the UP and DOWN Arrows and press ENTER to make selection
            </span>
        );
    }
};

const DocumentTypeList = ({
    documentTypes,
    highlightedDocumentType,
    selectDocumentType,
    changeDocumentTypesVisibility,
    changeDocumentTypeFilter
}) => {
    const renderedDocumentTypes = lodash.map(documentTypes,
        dt => <DocumentType key={dt.id}
            documentType={dt}
            highlightedDocumentType={highlightedDocumentType}
            selectDocumentType={selectDocumentType}
            changeDocumentTypeFilter={changeDocumentTypeFilter}
            changeDocumentTypesVisibility={changeDocumentTypesVisibility} />
    );
    return (
        <div className={styles.documentTypeList} data-cy="documentTypePicker-renderedDocumentTypes">
            {renderedDocumentTypes}
        </div>
    );
};

const DocumentType = ({
    documentType,
    highlightedDocumentType,
    selectDocumentType,
    changeDocumentTypesVisibility,
    changeDocumentTypeFilter
}) => {
    const { id, displayName } = documentType;
    const className = highlightedDocumentType && highlightedDocumentType.id === documentType.id
        ? styles.documentTypeItemSelected
        : styles.documentTypeItem;
    const handleClick = e => {
        e.stopPropagation();
        selectDocumentType(id);
        changeDocumentTypesVisibility(false);
        changeDocumentTypeFilter('');
    };
    return <div className={className} onClick={handleClick} data-cy={'documentTypePicker-' + id}>{displayName}</div>;
};

export default connect(state => {
    return {
        highlightedDocumentType: getHighlightedDocumentType(state)
    };
}, {
    changeDocumentTypeFilter,
    changeDocumentTypesVisibility,
    highlightDocumentType,
    selectDocumentType,
    deselectDocumentType
})(DocumentTypePicker);
